import { Config } from '@adornis/config/config.js';
import { ExamType } from '@ils/static/enums.js';
import { readFileSync } from 'node:fs';

/**
 * Interfaces for the overall project configuration
 * Each exam type has its own configuration with stripe and company info.
 * We split this config into server side and client side parts that the user can't / can see
 */
type ProjectConfig = Record<ExamType, ServerSideExamTypeConfig & ClientSideExamTypeConfig>;
type ServerSideConfig = Record<ExamType, ServerSideExamTypeConfig>;
type ClientSideConfig = Record<ExamType, ClientSideExamTypeConfig>;

export interface ServerSideExamTypeConfig {
  stripeInfo: StripeConfig;
  virtualBadgeConfig: VirtualBadgeConfig;
}

export interface ClientSideExamTypeConfig {
  companyInfo: CompanyInfo;
}

export interface StripeConfig {
  stripePrivateKey: string;
  stripePublishableKey: string;
  stripeWebhookSecret: string;
}

export interface VirtualBadgeConfig {
  certificateID: string;
}

export interface CompanyInfo {
  name: string;
  street: string;
  city: string;
  manager: string;
  email: string;
  taxNumber: string;
  taxAmountPercent: string;
  taxID: string;
  commercialRegistry: string;
  bank: string;
  iban: string;
  bic: string;
  signupUrl: string;
  agbUrl: string;
  dataPrivacyUrl: string;
  paymentSuccessUrl: string;
  paymentFailureUrl: string;
}
// Used to validate companyInfo on startup
const requiredCompanyInfoFields = [
  'name',
  'street',
  'city',
  'manager',
  'email',
  'taxNumber',
  'taxAmountPercent',
  'taxID',
  'commercialRegistry',
  'bank',
  'iban',
  'bic',
  'signupUrl',
  'agbUrl',
  'dataPrivacyUrl',
  'paymentSuccessUrl',
  'paymentFailureUrl',
];
export const projectConfig = new Config('projectConfig', {
  //We parse the PROJECT_CONFIG env var into server side and client side parts that the user can't / can see
  SERVER_SIDE_CONFIG: {
    client: false,
    env: true,
    name: 'PROJECT_CONFIG',
    required: true,
    parse: (value: string) => {
      if (typeof value !== 'string' || value.startsWith('{')) {
        return parseServerSideConfig(value);
      }
      const configContent = readFileSync(value, 'utf-8');
      return parseServerSideConfig(configContent);
    },
  },
  CLIENT_SIDE_CONFIG: {
    client: true,
    env: true,
    name: 'PROJECT_CONFIG',
    required: true,
    parse: (value: string) => {
      if (typeof value !== 'string' || value.startsWith('{')) {
        return parseClientSideConfig(value);
      }
      const configContent = readFileSync(value, 'utf-8');
      return parseClientSideConfig(configContent);
    },
  },
});

/**
 * Get Stripe configuration for a specific exam type
 * @param {string} examType - The exam type to get configuration for (e.g., ExamType.FAMED)
 * @returns {StripeConfig} The Stripe configuration for the specified exam type
 */
export function getStripeApiConfig(examType: ExamType): StripeConfig {
  return projectConfig.get('SERVER_SIDE_CONFIG')[examType].stripeInfo;
}

/**
 * Get company information for a specific exam type
 * @param {ExamType} examType - The exam type to get company information for
 * @returns {CompanyInfo} The company information for the specified exam type
 */
export function getCompanyInfo(examType: ExamType): CompanyInfo {
  return projectConfig.get('CLIENT_SIDE_CONFIG')[examType].companyInfo;
}

/**
 * Get virtual badge configuration for a specific exam type
 * @param {ExamType} examType - The exam type to get virtual badge configuration for
 * @returns {VirtualBadgeConfig} The virtual badge configuration for the specified exam type
 */
export function getVirtualBadgeConfig(examType: ExamType): VirtualBadgeConfig {
  return projectConfig.get('SERVER_SIDE_CONFIG')[examType].virtualBadgeConfig;
}

/* Validation functions below */
function validateServerSideConfig(examType: ExamType, examConfig?: Partial<ServerSideExamTypeConfig>): void {
  if (!examConfig) {
    throw new Error(`No configuration found for exam type ${examType}`);
  }
  if (!examConfig.stripeInfo) {
    throw new Error(`No stripe info found for exam type ${examType}`);
  }
  if (!examConfig.virtualBadgeConfig) {
    throw new Error(`No virtual badge config found for exam type ${examType}`);
  }
}

function validateClientSideConfig(examType: ExamType, examConfig?: Partial<ClientSideExamTypeConfig>): void {
  if (!examConfig) {
    throw new Error(`No configuration found for exam type ${examType}`);
  }
  if (!examConfig.companyInfo) {
    throw new Error(`No company info found for exam type ${examType}`);
  }
}

function validateStripeApiConfig(examType: ExamType, examConfig?: Partial<ServerSideExamTypeConfig>): void {
  const stripeInfo = examConfig?.stripeInfo;
  if (!stripeInfo?.stripePrivateKey) {
    throw new Error(`No stripe private key found for exam type ${examType}`);
  }
  if (!stripeInfo.stripePublishableKey) {
    throw new Error(`No stripe publishable key found for exam type ${examType}`);
  }
  if (!stripeInfo.stripeWebhookSecret) {
    throw new Error(`No stripe webhook secret found for exam type ${examType}`);
  }
}

function validateCompanyInfo(examType: ExamType, examConfig?: Partial<ClientSideExamTypeConfig>): void {
  // Validate companyInfo (required fields only)
  const companyInfo = examConfig?.companyInfo;
  requiredCompanyInfoFields.forEach(field => {
    if (!companyInfo?.[field]) {
      throw new Error(`Missing ${field} in companyInfo for exam type ${examType}`);
    }
  });
}

function validateVirtualBadgeConfig(examType: ExamType, examConfig?: Partial<ServerSideExamTypeConfig>): void {
  const virtualBadgeConfig = examConfig?.virtualBadgeConfig;
  if (!virtualBadgeConfig?.certificateID) {
    throw new Error(`No virtual badge certificateID found for exam type ${examType}`);
  }
}

/* Parsing functions */
function parseRawProjectConfig(value: string): ProjectConfig {
  return typeof value === 'string' ? (JSON.parse(value) as ProjectConfig) : (value as ProjectConfig);
}

function parseServerSideConfig(value: string): ServerSideConfig {
  const config = parseRawProjectConfig(value);

  //Initialize
  const parsedConfig = Object.fromEntries(
    Object.values(ExamType).map(examType => [
      examType,
      {
        stripeInfo: config[examType].stripeInfo,
        virtualBadgeConfig: config[examType].virtualBadgeConfig,
      },
    ]),
  ) as ServerSideConfig;

  //Validate
  Object.values(ExamType).forEach(examType => {
    validateServerSideConfig(examType, parsedConfig[examType]);
    validateStripeApiConfig(examType, parsedConfig[examType]);
    validateVirtualBadgeConfig(examType, parsedConfig[examType]);
  });

  return parsedConfig;
}

function parseClientSideConfig(value: string): ClientSideConfig {
  const config = parseRawProjectConfig(value);

  //Initialize
  const parsedConfig = Object.fromEntries(
    Object.values(ExamType).map(examType => [
      examType,
      {
        companyInfo: config[examType].companyInfo,
      },
    ]),
  ) as ClientSideConfig;

  //Validate
  Object.values(ExamType).forEach(examType => {
    validateClientSideConfig(examType, parsedConfig[examType]);
    validateCompanyInfo(examType, parsedConfig[examType]);
  });

  return parsedConfig;
}
